import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Loader2, Flame, Building2, AlertCircle, GitMerge, CheckCircle2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { dashApi } from "../api";
import { useDashAuth } from "../AuthContext";
import { STAGE_LABEL, STAGE_BADGE, formatRelative, isReadOnly } from "../utils";

const formatMoney = (n) => {
  if (!n) return "$0";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}k`;
  return `$${n}`;
};

export default function CompaniesPage() {
  const navigate = useNavigate();
  const { user } = useDashAuth();
  const isAdmin = user?.role === "admin";
  const readOnly = isReadOnly(user);
  const [q, setQ] = useState("");
  const [needsName, setNeedsName] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  // Auto-merge scan state: null = not scanned, [] = scanned, no matches,
  // [candidates…] = ready to review. running = scan or bulk-merge in flight.
  const [autoMergeCandidates, setAutoMergeCandidates] = useState(null);
  const [autoMergeRunning, setAutoMergeRunning] = useState(false);
  const [autoMergeOpen, setAutoMergeOpen] = useState(false);
  const debounceRef = useRef(null);

  const load = useCallback(async (query, needsNameFlag) => {
    setLoading(true);
    setErr("");
    try {
      const params = {};
      if (query) params.q = query;
      if (needsNameFlag) params.needs_name = true;
      const { data } = await dashApi.get("/companies", { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setErr(e.message || "Failed to load companies");
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => { load("", false); }, [load]);

  // Debounce search-as-you-type (300ms). Also re-runs when the placeholder
  // filter toggle changes so the queue updates instantly.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      load(q.trim(), needsName);
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q, needsName, load]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    load(q.trim(), needsName);
  };

  const scanForMerges = useCallback(async () => {
    setAutoMergeRunning(true);
    setAutoMergeOpen(true);
    try {
      const { data } = await dashApi.get("/companies/_placeholder-candidates");
      setAutoMergeCandidates(data.candidates || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Scan failed");
      setAutoMergeCandidates([]);
    } finally {
      setAutoMergeRunning(false);
    }
  }, []);

  const runBulkMerge = useCallback(async () => {
    if (!autoMergeCandidates || autoMergeCandidates.length === 0) return;
    setAutoMergeRunning(true);
    try {
      const pairs = autoMergeCandidates.map((c) => ({
        source_id: c.placeholder.id,
        target_id: c.target.id,
      }));
      const { data } = await dashApi.post("/companies/bulk-merge", { pairs });
      toast.success(
        `Merged ${data.succeeded} placeholder${data.succeeded === 1 ? "" : "s"}${
          data.failed ? ` · ${data.failed} failed` : ""
        }`,
      );
      setAutoMergeOpen(false);
      setAutoMergeCandidates(null);
      await load(q.trim(), needsName);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Bulk merge failed");
    } finally {
      setAutoMergeRunning(false);
    }
  }, [autoMergeCandidates, load, q, needsName]);

  return (
    <div className="space-y-5" data-testid="dashboard-companies">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Companies</h1>
          <p className="text-sm text-slate-500 mt-1">
            {needsName
              ? `${total} placeholder ${total === 1 ? "company needs" : "companies need"} a real name.`
              : `Submissions grouped by company. ${total} ${total === 1 ? "company" : "companies"}.`}
          </p>
        </div>
        <form onSubmit={onSubmit} className="flex gap-2 items-center flex-wrap">
          <button
            type="button"
            onClick={() => setNeedsName((v) => !v)}
            data-testid="companies-needs-name-toggle"
            className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${
              needsName
                ? "bg-amber-100 border-amber-300 text-amber-900 hover:bg-amber-200"
                : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"
            }`}
            title="Show only placeholder companies (acct # captured but no company name yet)"
          >
            <AlertCircle className="h-3.5 w-3.5" />
            Needs company name
          </button>
          {needsName && isAdmin && !readOnly && (
            <button
              type="button"
              onClick={scanForMerges}
              disabled={autoMergeRunning}
              data-testid="companies-auto-merge-btn"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Scan placeholders and offer one-click merges into companies with matching names"
            >
              {autoMergeRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <GitMerge className="h-3.5 w-3.5" />}
              Auto-merge duplicates
            </button>
          )}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="companies-search"
              placeholder="Search company…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-8 w-64"
            />
          </div>
          <button type="submit" className="sr-only">Search</button>
        </form>
      </div>

      {err && (
        <div className="text-rose-700 bg-rose-50 border border-rose-200 p-3 rounded-md">{err}</div>
      )}

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-6 flex items-center gap-2 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-slate-500" data-testid="companies-empty">
            <Building2 className="h-8 w-8 mx-auto text-slate-300 mb-2" />
            <p className="text-sm">
              {needsName
                ? "No placeholders left — every company on file has a real name."
                : "No companies found."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 border-b border-slate-200">
                <tr>
                  <th className="text-left font-medium px-4 py-2.5">Company</th>
                  <th className="text-left font-medium px-4 py-2.5">Primary contact</th>
                  <th className="text-left font-medium px-4 py-2.5">Stage</th>
                  <th className="text-right font-medium px-4 py-2.5">Subs</th>
                  <th className="text-right font-medium px-4 py-2.5">Pipeline $</th>
                  <th className="text-left font-medium px-4 py-2.5">Last activity</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr
                    key={c.key}
                    onClick={() => navigate(`/dashboard/companies/${c.key}`)}
                    className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer"
                    data-testid={`company-row-${c.key}`}
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={`/dashboard/companies/${c.key}`}
                        onClick={(e) => e.stopPropagation()}
                        className="font-medium text-slate-900 hover:underline inline-flex items-center gap-1.5"
                      >
                        {c.company}
                        {c.hot_count > 0 && <Flame className="h-3.5 w-3.5 text-rose-500" />}
                      </Link>
                      {c.is_name_placeholder && (
                        <div className="mt-1">
                          <span
                            data-testid={`company-placeholder-pill-${c.key}`}
                            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800"
                            title="This Company row was auto-created from an account number — click through to add the real name."
                          >
                            <AlertCircle className="h-3 w-3" />
                            Needs name
                            {c.account_number ? ` · Acct ${c.account_number}` : ""}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-900">{c.primary_contact || "—"}</div>
                      <div className="text-xs text-slate-500">{c.primary_email || ""}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded-full border ${STAGE_BADGE[c.dominant_stage] || "bg-slate-100"}`}>
                        {STAGE_LABEL[c.dominant_stage] || c.dominant_stage}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-slate-800">
                      {c.submission_count}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-slate-800">
                      {formatMoney(c.estimated_value)}
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                      {formatRelative(c.last_activity)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {autoMergeOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          role="dialog"
          aria-modal="true"
          data-testid="auto-merge-dialog"
          onClick={() => !autoMergeRunning && setAutoMergeOpen(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="h-9 w-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
                <GitMerge className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-heading font-semibold text-slate-900">Auto-merge placeholders</h3>
                <p className="text-sm text-slate-600 mt-1">
                  {autoMergeRunning && autoMergeCandidates == null
                    ? "Scanning placeholders for exact name matches…"
                    : (autoMergeCandidates?.length || 0) === 0
                      ? "No merge candidates found. Every placeholder is either unique or has no company name on its submissions yet."
                      : `Found ${autoMergeCandidates.length} placeholder${autoMergeCandidates.length === 1 ? "" : "s"} whose linked submissions point to an existing Company. Review and confirm to merge them all in one shot.`}
                </p>
              </div>
            </div>

            {autoMergeCandidates && autoMergeCandidates.length > 0 && (
              <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md mb-4">
                {autoMergeCandidates.map((c) => (
                  <li
                    key={c.placeholder.id}
                    className="p-3 flex items-center gap-3 text-sm"
                    data-testid={`auto-merge-candidate-${c.placeholder.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-amber-900 truncate">{c.placeholder.name}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {c.placeholder.submission_count} submission{c.placeholder.submission_count === 1 ? "" : "s"}
                        {c.placeholder.account_number ? ` · Acct ${c.placeholder.account_number}` : ""}
                      </div>
                    </div>
                    <GitMerge className="h-4 w-4 text-slate-400 shrink-0" />
                    <div className="flex-1 min-w-0 text-right">
                      <div className="font-medium text-slate-900 truncate">{c.target.name}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {c.target.submission_count} existing submission{c.target.submission_count === 1 ? "" : "s"}
                        {c.sample_name && c.sample_name !== c.target.name
                          ? ` · match on "${c.sample_name}"`
                          : ""}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setAutoMergeOpen(false)}
                disabled={autoMergeRunning}
                data-testid="auto-merge-close"
              >
                {(autoMergeCandidates?.length || 0) === 0 ? "Close" : "Cancel"}
              </Button>
              {autoMergeCandidates && autoMergeCandidates.length > 0 && (
                <Button
                  type="button"
                  size="sm"
                  onClick={runBulkMerge}
                  disabled={autoMergeRunning}
                  data-testid="auto-merge-confirm"
                >
                  {autoMergeRunning
                    ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    : <CheckCircle2 className="h-4 w-4 mr-2" />}
                  Merge {autoMergeCandidates.length} placeholder{autoMergeCandidates.length === 1 ? "" : "s"}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
